import { useState, type FormEvent } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuthStore } from '../stores/authStore';
import MfaEnrollScreen from './MfaEnrollScreen';
import MfaChallengeScreen from './MfaChallengeScreen';

interface LocationState {
  from?: string;
}

/**
 * Unified /login route. Delegates to sub-screens based on `authStatus` so
 * the URL stays `/login` through the entire sign-in flow — matches the
 * existing `RequireAdmin` redirect contract, which only ever targets
 * `/login`.
 */
export default function Login() {
  const location = useLocation();

  const token = useAuthStore((s) => s.token);
  const status = useAuthStore((s) => s.status);
  const error = useAuthStore((s) => s.error);
  const rememberMe = useAuthStore((s) => s.rememberMe);
  const setRememberMe = useAuthStore((s) => s.setRememberMe);
  const submitPassword = useAuthStore((s) => s.submitPassword);

  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');

  const from = (location.state as LocationState | null)?.from ?? '/';

  // App still probing for an existing session via /admin-refresh — show a
  // neutral spinner instead of flashing the login form. Once hydrate()
  // resolves, status flips to either authenticated (we Navigate away below)
  // or unauthenticated (the form renders).
  if (status === 'hydrating') {
    return (
      <div
        className="min-h-screen flex items-center justify-center bg-slate-100 px-4"
        data-testid="login-hydrating"
      >
        <div className="text-sm text-slate-500">Restoring session…</div>
      </div>
    );
  }

  // Already logged in — bounce out of /login.
  if (token) {
    return <Navigate to={from} replace />;
  }

  if (status === 'mfa_enroll_pending') {
    return <MfaEnrollScreen redirectTo={from} />;
  }

  if (status === 'mfa_challenge') {
    return <MfaChallengeScreen redirectTo={from} />;
  }

  async function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    try {
      await submitPassword({ username: username.trim(), password });
      // No navigation yet — the sub-screens handle the rest of the flow.
    } catch {
      // Error surfaced via authStore.error; nothing else to do.
    }
  }

  const submitting = status === 'authenticating';

  return (
    <div
      className="min-h-screen flex items-center justify-center bg-slate-100 px-4"
      data-testid="login-screen"
    >
      <form
        onSubmit={handleSubmit}
        className="w-full max-w-sm bg-white rounded-lg shadow p-6 space-y-5"
        aria-labelledby="login-title"
      >
        <div className="text-center space-y-1">
          <div
            aria-hidden="true"
            className="mx-auto w-12 h-12 rounded-full bg-slate-800 text-slate-100 flex items-center justify-center font-bold"
          >
            WC
          </div>
          <h1
            id="login-title"
            className="text-xl font-semibold text-slate-900"
          >
            Word Clash Admin
          </h1>
          <p className="text-sm text-slate-500">
            Sign in with your admin credentials.
          </p>
        </div>

        <div className="space-y-4">
          <label className="block text-sm">
            <span className="text-slate-700">Username</span>
            <input
              type="text"
              autoComplete="username"
              autoFocus
              required
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              disabled={submitting}
              data-testid="login-username"
              className="mt-1 block w-full rounded border border-slate-300 px-3 py-2 text-slate-900 focus:outline-none focus:ring-2 focus:ring-slate-400 disabled:opacity-50"
            />
          </label>

          <label className="block text-sm">
            <span className="text-slate-700">Password</span>
            <input
              type="password"
              autoComplete="current-password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              disabled={submitting}
              data-testid="login-password"
              className="mt-1 block w-full rounded border border-slate-300 px-3 py-2 text-slate-900 focus:outline-none focus:ring-2 focus:ring-slate-400 disabled:opacity-50"
            />
          </label>

          <label
            className="flex items-center gap-2 text-sm text-slate-700 select-none cursor-pointer"
            data-testid="login-remember-me-label"
          >
            <input
              type="checkbox"
              checked={rememberMe}
              onChange={(e) => setRememberMe(e.target.checked)}
              disabled={submitting}
              data-testid="login-remember-me"
              className="h-4 w-4 rounded border-slate-300 text-slate-800 focus:ring-slate-400"
            />
            <span>
              Keep me signed in for 30 days{' '}
              <span className="text-slate-400 text-xs">
                (otherwise: 24 hours)
              </span>
            </span>
          </label>
        </div>

        {error && (
          <div
            role="alert"
            data-testid="login-error"
            className="text-sm text-red-700 bg-red-50 border border-red-200 rounded px-3 py-2"
          >
            {error}
          </div>
        )}

        <button
          type="submit"
          disabled={submitting}
          data-testid="login-submit"
          className="w-full rounded bg-slate-800 text-white py-2 font-medium hover:bg-slate-700 focus:outline-none focus:ring-2 focus:ring-slate-400 disabled:opacity-50"
        >
          {submitting ? 'Signing in…' : 'Sign in'}
        </button>
      </form>
    </div>
  );
}
